import { supabase } from "@/platform/lib/supabase";
import { applySM2, defaultState, type SM2State } from "@/platform/learning/sm2";

export interface UspReviewQuestion {
  id: string;
  question_id: string;
  due_at: string;
  ease_factor: number;
  interval_days: number;
  repetitions: number;
  question: {
    id: string;
    number: number | null;
    kind: "objective" | "discursive" | "essay";
    stem: string;
    alternatives: { A?: string; B?: string; C?: string; D?: string; E?: string } | null;
    correct_answer: string | null;
    discipline: string;
    topic: string | null;
    year: number;
  };
}

export async function countDueUspReviews(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from("usp_reviews")
    .select("id", { count: "exact", head: true })
    .eq("user_id", userId)
    .lte("due_at", new Date().toISOString());
  if (error) {
    console.error("[usp-review] count", error);
    return 0;
  }
  return count ?? 0;
}

export async function loadDueUspReviews(userId: string, limit = 20): Promise<UspReviewQuestion[]> {
  const { data, error } = await supabase
    .from("usp_reviews")
    .select(
      "id, question_id, due_at, ease_factor, interval_days, repetitions, usp_questions!inner(id, number, kind, stem, alternatives, correct_answer, discipline, topic, year)",
    )
    .eq("user_id", userId)
    .lte("due_at", new Date().toISOString())
    .order("due_at", { ascending: true })
    .limit(limit);
  if (error) throw error;

  type Raw = Omit<UspReviewQuestion, "question"> & { usp_questions: UspReviewQuestion["question"] };
  return (data as unknown as Raw[]).map((r) => ({
    id: r.id,
    question_id: r.question_id,
    due_at: r.due_at,
    ease_factor: r.ease_factor,
    interval_days: r.interval_days,
    repetitions: r.repetitions,
    question: r.usp_questions,
  }));
}

export async function scheduleUspReviews(params: {
  userId: string;
  questionIds: string[];
}): Promise<void> {
  if (params.questionIds.length === 0) return;
  const init = defaultState();
  const rows = params.questionIds.map((qid) => ({
    user_id: params.userId,
    question_id: qid,
    ease_factor: init.ease_factor,
    interval_days: init.interval_days,
    repetitions: init.repetitions,
    due_at: new Date().toISOString(),
  }));
  const { error } = await supabase
    .from("usp_reviews")
    .upsert(rows, { onConflict: "user_id,question_id", ignoreDuplicates: true });
  if (error) console.error("[usp-review] schedule", error);
}

export async function gradeUspReview(
  reviewId: string,
  grade: number,
  prev: SM2State,
): Promise<void> {
  const next = applySM2(prev, grade);
  const { error } = await supabase
    .from("usp_reviews")
    .update({
      ease_factor: next.ease_factor,
      interval_days: next.interval_days,
      repetitions: next.repetitions,
      due_at: next.due_at,
      last_reviewed_at: new Date().toISOString(),
      last_quality: next.last_grade,
    })
    .eq("id", reviewId);
  if (error) throw error;
}
